import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const LANGUAGES = [
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
];

type NavItem = {
  labelKey: string;
  href: string;
  children?: { labelKey: string; href: string }[];
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems: NavItem[] = [
    {
      labelKey: 'nav_hotel_intro',
      href: '/greeting',
      children: [
        { labelKey: 'nav_greeting', href: '/greeting' },
        { labelKey: 'nav_contact', href: '/directions' },
      ],
    },
    { labelKey: 'nav_rooms', href: '/rooms' },
    { labelKey: 'nav_services', href: '/facilities' },
    { labelKey: 'nav_nearby', href: '/nearby' },
    { labelKey: 'nav_refund', href: '/refund' },
  ];

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="https://storage.readdy-site.link/project_files/f1a4733f-f142-44b4-9a4b-319ee3c3a442/cf5d4cea-d623-4733-86a8-803e415ede74_20161028_-yeong-bin-ho--ro-go_ol1574127236.png?v=7dd96618540c35f269119a9b3765e2fd"
            alt="YOUNGBIN HOTEL"
            className={`h-12 md:h-16 object-contain transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'}`}
          />
        </Link>

        {/* Desktop Nav */}
        <ul ref={dropdownRef} className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <li
              key={item.labelKey}
              className="relative"
              onMouseEnter={() => item.children && setOpenDropdown(item.labelKey)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to={item.href}
                className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-amber-700' : 'text-white hover:text-amber-200'
                }`}
              >
                {t(item.labelKey)}
                {item.children && (
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${openDropdown === item.labelKey ? 'rotate-180' : ''}`} />
                )}
              </Link>

              {/* Dropdown */}
              {item.children && openDropdown === item.labelKey && (
                <div className="absolute left-0 top-full pt-2 z-50">
                  <div className="bg-white rounded-lg border border-gray-100 py-1.5 min-w-[130px] overflow-hidden">
                    {item.children.map((child) => (
                      <Link
                        key={child.labelKey}
                        to={child.href}
                        onClick={() => setOpenDropdown(null)}
                        className="block px-5 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {t(child.labelKey)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Right: Language + Reserve */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Switcher */}
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'border-gray-300 text-gray-600 hover:border-amber-600 hover:text-amber-700'
                  : 'border-white/50 text-white hover:border-white'
              }`}
            >
              <span>{currentLang.flag}</span>
              <span>{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg border border-gray-100 py-1 min-w-[130px] z-50">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangChange(lang.code)}
                    className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-amber-50 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap ${
                      lang.code === i18n.language ? 'text-amber-700 font-semibold bg-amber-50' : 'text-gray-700'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/reservation"
            className={`text-xs px-4 py-2 rounded-full border whitespace-nowrap cursor-pointer transition-all ${
              scrolled
                ? 'border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white'
                : 'border-white text-white hover:bg-white hover:text-amber-800'
            }`}
          >
            {t('nav_reserve')}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="메뉴"
        >
          <span className={`block w-6 h-0.5 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
          <span className={`block w-6 h-0.5 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
          <span className={`block w-6 h-0.5 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-0.5">
          {navItems.map((item) => (
            <div key={item.labelKey}>
              <Link
                to={item.href}
                className="block py-2 text-xs font-medium text-gray-800 hover:text-amber-700 cursor-pointer"
                onClick={() => !item.children && setMenuOpen(false)}
              >
                {t(item.labelKey)}
              </Link>
              {item.children && (
                <div className="pl-3 flex flex-col gap-0.5 border-l-2 border-amber-100 ml-1 mb-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.labelKey}
                      to={child.href}
                      className="block py-1.5 text-xs text-gray-500 hover:text-amber-700 cursor-pointer"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t(child.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Mobile Language Switcher */}
          <div className="flex gap-1.5 flex-wrap pt-2.5 mt-1 border-t border-gray-100">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { handleLangChange(lang.code); setMenuOpen(false); }}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border cursor-pointer whitespace-nowrap transition-all ${
                  lang.code === i18n.language
                    ? 'border-amber-600 text-amber-700 bg-amber-50 font-semibold'
                    : 'border-gray-200 text-gray-600 hover:border-amber-400'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
          <Link
            to="/reservation"
            className="mt-2 text-xs text-center px-4 py-2 rounded-full bg-amber-700 text-white cursor-pointer whitespace-nowrap"
            onClick={() => setMenuOpen(false)}
          >
            {t('nav_reserve')}
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
